import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, Progress, Tag, Space, Card, Badge } from 'antd';
import { DropboxOutlined, CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useInboundAcceptance } from '@contexts/operator/InboundAcceptanceContext';
import GlobalBarcodeInput from '@components/operator/GlobalBarcodeInput';
import { useBarcodeInput, useBarcodeInputContext } from '@contexts/operator/BarcodeInputContext';

const { Title, Text } = Typography;

export default function ScanPanel() {
  const { scanBarcode, scannedItem, inbound, activeReceipt } = useInboundAcceptance();
  const { t } = useTranslation();
  const { clear: clearBarcodeInput } = useBarcodeInputContext();

  useEffect(() => {
    if (!scannedItem) {
      clearBarcodeInput();
    }
  }, [scannedItem, clearBarcodeInput]);

  const handleScan = async (code) => {
    const result = await scanBarcode(code);
    if (!result?.success) {
      clearBarcodeInput();
    }
    return true;
  };

  // 條件式註冊條碼處理器：只在 drawer 開啟時禁用，避免重複掃描。
  // Receipt 會在 openVerification 時 lazy 建立，不需要事前條件。
  const isDisabled = !!scannedItem;
  useBarcodeInput(isDisabled ? null : handleScan);

  // Calculate stats
  const stats = useMemo(() => {
    if (!inbound?.items) return { total: 0, verified: 0, percent: 0, totalItems: 0, verifiedItems: 0 };

    const items = inbound.items;
    const total = items.reduce((sum, item) => sum + (item.scheduled_quantity || 0), 0);
    const verified = items.reduce((sum, item) => sum + (item.received_quantity || 0), 0);
    const percent = total > 0 ? Math.round((verified / total) * 100) : 0;

    const totalItems = items.length;
    const verifiedItems = items.filter(i => (i.received_quantity || 0) >= (i.scheduled_quantity || 0)).length;

    return { total, verified, percent, totalItems, verifiedItems };
  }, [inbound]);

  // Receipt history
  const receipts = useMemo(() => {
    return inbound?.receipts || [];
  }, [inbound]);

  const receiptStatusConfig = {
    receiving: { color: 'processing', text: t('operator.receipt.status_receiving', { defaultValue: '驗收中' }), tagColor: 'green' },
    pending_putaway: { color: 'warning', text: t('operator.receipt.status_pending_putaway', { defaultValue: '待上架' }), tagColor: 'orange' },
    putting_away: { color: 'processing', text: t('operator.receipt.status_putting_away', { defaultValue: '上架中' }), tagColor: 'blue' },
    done: { color: 'success', text: t('operator.receipt.status_done', { defaultValue: '已完成' }), tagColor: 'default' },
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#fff', borderLeft: '1px solid #f0f0f0' }}>

      {/* 1. Scan Section */}
      <div style={{ padding: '24px', background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
        <Title level={4} style={{ marginBottom: '16px' }}>{t('operator.scan_barcode', { defaultValue: '掃描條碼' })}</Title>
        <GlobalBarcodeInput
          placeholder={t('operator.scan_placeholder', { defaultValue: '請掃描商品條碼' })}
        />

        <div style={{ marginTop: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <Text strong>{t('operator.progress', { defaultValue: '驗收進度' })}</Text>
            <Text type="secondary">{stats.verifiedItems} / {stats.totalItems} {t('common.items', { defaultValue: '項目' })}</Text>
          </div>
          <Progress percent={stats.percent} status="active" strokeColor="#52c41a" />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '12px', color: '#888' }}>
            <span>{t('common.received_qty', { defaultValue: '已驗收數量' })}: {stats.verified}</span>
            <span>{t('common.total_qty', { defaultValue: '總數量' })}: {stats.total}</span>
          </div>
        </div>
      </div>

      {/* 2. Receipt History */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '16px 24px', borderBottom: '1px solid #f0f0f0', background: '#fff' }}>
          <Title level={5} style={{ margin: 0 }}>
            {t('operator.receipt.history', { defaultValue: '驗收批次' })} ({receipts.length})
          </Title>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 24px' }}>
          {receipts.length > 0 ? (
            <Space direction="vertical" style={{ width: '100%' }} size={8}>
              {receipts.map((receipt) => {
                const config = receiptStatusConfig[receipt.status] || receiptStatusConfig.done;
                const isActive = receipt.id === activeReceipt?.id;

                return (
                  <Card
                    key={receipt.id}
                    size="small"
                    style={{
                      border: isActive ? '1px solid #52c41a' : undefined,
                      background: isActive ? '#f6ffed' : undefined
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Space>
                        <Badge status={config.color} />
                        <Text strong style={{ fontSize: 13 }}>{receipt.name}</Text>
                        <Tag color={config.tagColor} style={{ fontSize: 11 }}>{config.text}</Tag>
                      </Space>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {receipt.total_items || 0} {t('common.items_unit', { defaultValue: '項' })} / {receipt.total_received_quantity || 0} {t('common.pieces_unit', { defaultValue: '件' })}
                      </Text>
                    </div>
                    <div style={{ marginTop: 4, fontSize: 11, color: '#999' }}>
                      <Space size={4}>
                        <ClockCircleOutlined />
                        {dayjs(receipt.created_at).format('MM-DD HH:mm')}
                      </Space>
                    </div>
                  </Card>
                );
              })}
            </Space>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#ccc' }}>
              <DropboxOutlined style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }} />
              <Text type="secondary">{t('operator.no_activity', { defaultValue: '尚無驗收紀錄' })}</Text>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
